import { useDeferredValue, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Search,
  Store,
  UsersRound,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { AdminShopListItem, AdminShopsResponse } from '@agenda-facil/contracts';
import { Button, Input, Surface } from '@agenda-facil/ui';
import { ErrorState } from '../../components/query-feedback';
import { shortDate } from '../../features/admin/admin-format';
import { AdminStatusPill } from '../../features/admin/admin-status-pill';
import { api } from '../../lib/api';
import { AdminPageHeader } from './admin-page-header';

export function AdminShopsPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const deferredSearch = useDeferredValue(search.trim());
  const shops = useQuery({
    queryKey: ['admin', 'shops', page, deferredSearch],
    queryFn: () =>
      api.get<AdminShopsResponse>(
        `/admin/shops?page=${page}&pageSize=10&search=${encodeURIComponent(deferredSearch)}`,
      ),
    placeholderData: keepPreviousData,
  });

  const result = shops.data;
  return (
    <div>
      <AdminPageHeader
        title="Barbearias"
        description="Clientes SaaS, situação comercial e volume de uso em uma única lista."
      />
      <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <label className="relative block w-full sm:max-w-md" htmlFor="admin-shop-search">
          <span className="sr-only">Buscar barbearias</span>
          <Search
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]"
            size={18}
          />
          <Input
            id="admin-shop-search"
            className="pl-11"
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Nome, responsável, e-mail ou telefone"
          />
        </label>
        <p className="text-sm font-bold text-[var(--muted-foreground)]">
          {result
            ? `${result.pagination.total} ${result.pagination.total === 1 ? 'barbearia' : 'barbearias'}`
            : 'Atualizando…'}
        </p>
      </div>

      {shops.isLoading ? (
        <AdminShopsSkeleton />
      ) : shops.isError || !result ? (
        <div className="mt-6">
          <ErrorState
            message="Não foi possível carregar as barbearias."
            retry={() => void shops.refetch()}
          />
        </div>
      ) : result.items.length === 0 ? (
        <Surface className="mt-6 grid min-h-72 place-items-center px-6 text-center">
          <div className="max-w-md">
            <Store className="mx-auto text-[var(--muted-foreground)]" size={30} />
            <h2 className="mt-4 text-lg font-black">
              {deferredSearch ? 'Nenhum resultado' : 'Ainda não há barbearias'}
            </h2>
            <p className="mt-2 text-sm leading-6 text-[var(--muted-foreground)]">
              {deferredSearch
                ? 'Tente buscar por outro nome, responsável ou telefone.'
                : 'As contas aparecerão aqui depois que os proprietários concluírem o cadastro.'}
            </p>
          </div>
        </Surface>
      ) : (
        <>
          <Surface className="mt-6 overflow-hidden">
            <div className="hidden overflow-x-auto lg:block">
              <table className="w-full min-w-[960px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-[var(--border)] bg-[var(--surface-raised)] text-xs font-black uppercase tracking-[.07em] text-[var(--muted-foreground)]">
                    <th className="px-5 py-4" scope="col">
                      Barbearia
                    </th>
                    <th className="px-5 py-4" scope="col">
                      Responsável
                    </th>
                    <th className="px-5 py-4" scope="col">
                      Situação
                    </th>
                    <th className="px-5 py-4" scope="col">
                      Uso
                    </th>
                    <th className="px-5 py-4" scope="col">
                      Cadastro
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {result.items.map((shop) => (
                    <ShopTableRow key={shop.id} shop={shop} />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="divide-y divide-[var(--border)] lg:hidden">
              {result.items.map((shop) => (
                <ShopMobileRow key={shop.id} shop={shop} />
              ))}
            </div>
          </Surface>
          <div className="mt-5 flex items-center justify-between gap-4">
            <Button
              variant="secondary"
              size="sm"
              disabled={page <= 1 || shops.isFetching}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              <ChevronLeft size={16} /> Anterior
            </Button>
            <p className="text-xs font-extrabold text-[var(--muted-foreground)]">
              Página {result.pagination.page} de {result.pagination.totalPages}
            </p>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= result.pagination.totalPages || shops.isFetching}
              onClick={() => setPage((current) => current + 1)}
            >
              Próxima <ChevronRight size={16} />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function ShopTableRow({ shop }: { shop: AdminShopListItem }) {
  return (
    <tr className="border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--surface-raised)]">
      <td className="px-5 py-4">
        <div className="flex items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-[var(--accent-soft)] text-sm font-black text-[var(--accent)]">
            {shop.name.slice(0, 1).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="font-extrabold">{shop.name}</p>
            <div className="mt-1 flex items-center gap-2 text-xs text-[var(--muted-foreground)]">
              <span>/{shop.slug}</span>
              {shop.published && (
                <Link
                  className="inline-flex items-center gap-1 font-bold text-[var(--accent)]"
                  target="_blank"
                  rel="noreferrer"
                  to={`/barbearia/${shop.slug}`}
                >
                  Página <ExternalLink size={12} />
                </Link>
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="px-5 py-4">
        <p className="text-sm font-bold">{shop.ownerName}</p>
        <p className="mt-1 text-xs text-[var(--muted-foreground)]">{shop.ownerEmail}</p>
      </td>
      <td className="px-5 py-4">
        <AdminStatusPill status={shop.status} />
        <p className="mt-2 text-xs text-[var(--muted-foreground)]">
          {shop.planName ?? (shop.status === 'TRIALING' ? 'Teste grátis' : 'Sem plano')}
        </p>
      </td>
      <td className="px-5 py-4">
        <div className="flex gap-4 text-xs font-bold text-[var(--muted-foreground)]">
          <span className="inline-flex items-center gap-1.5">
            <UsersRound size={15} /> {shop.professionals}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays size={15} /> {shop.appointments}
          </span>
        </div>
      </td>
      <td className="px-5 py-4 text-sm font-bold">{shortDate.format(new Date(shop.createdAt))}</td>
    </tr>
  );
}

function ShopMobileRow({ shop }: { shop: AdminShopListItem }) {
  return (
    <article className="p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-[var(--accent-soft)] text-sm font-black text-[var(--accent)]">
            {shop.name.slice(0, 1).toUpperCase()}
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-sm font-extrabold">{shop.name}</h2>
            <p className="truncate text-xs text-[var(--muted-foreground)]">{shop.ownerName}</p>
          </div>
        </div>
        <AdminStatusPill status={shop.status} />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-[var(--border)] pt-4 text-xs">
        <MobileDatum
          label="Plano"
          value={shop.planName ?? (shop.status === 'TRIALING' ? 'Teste grátis' : 'Sem plano')}
        />
        <MobileDatum label="Cadastro" value={shortDate.format(new Date(shop.createdAt))} />
        <MobileDatum label="Profissionais" value={shop.professionals.toString()} />
        <MobileDatum label="Agendamentos" value={shop.appointments.toString()} />
      </div>
    </article>
  );
}

function MobileDatum({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="font-bold text-[var(--muted-foreground)]">{label}</p>
      <p className="mt-1 font-extrabold">{value}</p>
    </div>
  );
}

function AdminShopsSkeleton() {
  return (
    <div
      className="mt-6 animate-pulse overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)]"
      aria-label="Carregando barbearias"
    >
      {[0, 1, 2, 3, 4].map((item) => (
        <div
          key={item}
          className="flex h-20 items-center gap-4 border-b border-[var(--border)] px-5 last:border-b-0"
        >
          <div className="size-10 rounded-xl bg-[var(--surface-raised)]" />
          <div className="h-3 w-44 rounded bg-[var(--surface-raised)]" />
          <div className="ml-auto h-7 w-24 rounded-full bg-[var(--surface-raised)]" />
        </div>
      ))}
    </div>
  );
}
